import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getUserById, updateUser, updateUserProfileById, sendPasswordResetEmail, setUserPassword, generateUserPassword } from '../../services/api';
import { COUNTRIES } from '../../constants/countries';

function AdminMemberEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    is_active: true,
    phone_number: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    job_title: '',
    company: '',
    website: '',
    bio: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        setError('');
        const userData = await getUserById(id);
        setUser(userData);

        setForm({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          is_active: userData.is_active !== undefined ? userData.is_active : true,
          phone_number: userData.profile?.phone_number || '',
          address: userData.profile?.address || '',
          city: userData.profile?.city || '',
          state: userData.profile?.state || '',
          postal_code: userData.profile?.postal_code || '',
          country: userData.profile?.country || '',
          job_title: userData.profile?.job_title || '',
          company: userData.profile?.company || '',
          website: userData.profile?.website || '',
          bio: userData.profile?.bio || '',
        });
      } catch (e) {
        setError(e.message || 'Failed to load member data');
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!form.first_name.trim()) {
      setError('First name is required');
      return;
    }
    if (!form.last_name.trim()) {
      setError('Last name is required');
      return;
    }
    if (form.website && form.website.trim() && !form.website.match(/^https?:\/\/.+/)) {
      setError('Website must be a valid URL starting with http:// or https://');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      // Update user basic info
      await updateUser(id, {
        first_name: form.first_name,
        last_name: form.last_name,
        is_active: form.is_active,
      });

      // Update profile info
      if (user.profile) {
        await updateUserProfileById(user.profile.id, {
          phone_number: form.phone_number,
          address: form.address,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: form.country,
          job_title: form.job_title,
          company: form.company,
          website: form.website,
          bio: form.bio,
        });
      }

      setSuccessMessage('Member updated successfully');

      // Reload user data to get fresh data
      const userData = await getUserById(id);
      setUser(userData);
    } catch (e) {
      setError(e.message || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    const confirmed = window.confirm(
      `Send password reset email to ${form.email}?\n\nThe user will receive an email with instructions to reset their password.`
    );
    if (!confirmed) return;

    try {
      setError('');
      setSuccessMessage('');
      setPasswordError('');
      await sendPasswordResetEmail(form.email);
      setSuccessMessage('Password reset email sent successfully');
    } catch (e) {
      setPasswordError(e.message || 'Failed to send password reset email');
    }
  };

  const handleGeneratePassword = async () => {
    try {
      setPasswordError('');
      const response = await generateUserPassword(id, 12);
      setPassword(response.password);
      setShowPassword(true);
    } catch (e) {
      setPasswordError(e.message || 'Failed to generate password');
    }
  };

  const handleUpdatePassword = async () => {
    if (!password) {
      setPasswordError('Please enter a password');
      return;
    }

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    const confirmed = window.confirm(
      `Set new password for ${form.email}?\n\nThe user's current password will be replaced.`
    );
    if (!confirmed) return;

    try {
      setPasswordError('');
      setSuccessMessage('');

      await setUserPassword(id, password);
      setSuccessMessage('Password updated successfully!');
      setPassword('');
      setShowPassword(false);
    } catch (e) {
      setPasswordError(e.message || 'Failed to update password');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-4">
        <div>Loading...</div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="p-4">
        <div className="text-red-600 mb-4">{error}</div>
        <Link to="/admin/members" className="text-blue-600 hover:text-blue-800 underline">
          ← Back to Members
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="mb-4">
        <Link to="/admin/members" className="text-blue-600 hover:text-blue-800 underline text-sm">
          ← Back to Members
        </Link>
      </div>

      <h1 className="text-2xl font-bold mb-6">Edit Member • {form.email}</h1>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* User Information Section */}
        <div className="bg-white rounded shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">User Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email (Read-only)
            </label>
            <input
              type="email"
              value={form.email}
              readOnly
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed from the admin panel</p>
          </div>

          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Account Active</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">Inactive users cannot log in</p>
          </div>
        </div>

        {/* Profile Information Section */}
        <div className="bg-white rounded shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Profile Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone_number"
                value={form.phone_number}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title
              </label>
              <input
                type="text"
                name="job_title"
                value={form.job_title}
                onChange={handleChange}
                placeholder="Software Engineer"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={handleChange}
              placeholder="Acme Corporation"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="123 Main St"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="Durham"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                name="state"
                value={form.state}
                onChange={handleChange}
                placeholder="NC"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postal Code
              </label>
              <input
                type="text"
                name="postal_code"
                value={form.postal_code}
                onChange={handleChange}
                placeholder="27701"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <select
              name="country"
              value={form.country}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a country</option>
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="url"
              name="website"
              value={form.website}
              onChange={handleChange}
              placeholder="https://yourwebsite.com"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bio
            </label>
            <textarea
              name="bio"
              value={form.bio}
              onChange={handleChange}
              rows="4"
              placeholder="Tell us about yourself..."
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Account Status Section (Read-only) */}
        <div className="bg-white rounded shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Account Status (Read-Only)</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Date Joined:</span>
              <p className="text-gray-900 mt-1">{formatDate(user?.date_joined)}</p>
            </div>

            <div>
              <span className="font-medium text-gray-700">Staff Status:</span>
              <p className="text-gray-900 mt-1">{user?.is_staff ? 'Yes' : 'No'}</p>
            </div>

            <div>
              <span className="font-medium text-gray-700">Superuser:</span>
              <p className="text-gray-900 mt-1">{user?.is_superuser ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>

        {/* Password Management Section */}
        <div className="bg-white rounded shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Password Management</h2>

          {passwordError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
              {passwordError}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password (min. 8 characters)"
                  className="w-full px-3 py-2 pr-20 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  {showPassword ? 'Hide' : 'Reveal'}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Password must be at least 8 characters and meet Django validation requirements
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleGeneratePassword}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Generate Password
            </button>
            <button
              type="button"
              onClick={handleUpdatePassword}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Update Password
            </button>
          </div>

          {/* Send Password Reset Email */}
          <div className="pt-6 mt-6 border-t border-gray-200">
            <h3 className="text-md font-medium mb-2">Send Password Reset Email</h3>
            <button
              type="button"
              onClick={handlePasswordReset}
              className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              Send Password Reset Email
            </button>
            <p className="text-sm text-gray-600 mt-2">
              The user will receive an email with instructions to reset their password
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <Link
            to="/admin/members"
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </Link>

          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminMemberEdit;
